package com.dotdrives.model

import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.put

/** The `automation_rules` insert payload shape (matches `RuleInsert` decoded server-side). */
@Serializable
data class RuleInsert(
    val id: String,
    @SerialName("org_id")
    val orgId: String,
    val name: String,
    @SerialName("trigger_verb")
    val triggerVerb: String?,
    @SerialName("trigger_conditions")
    val triggerConditions: JsonObject,
    @SerialName("action_config")
    val actionConfig: JsonObject,
)

/**
 * Builds the `automation_rules` insert payload from a draft. `AutomationRuleClient.create`
 * delegates to this and nothing else -- there is no separate, un-tested path that could skip
 * the guard below, unlike a plain inline `require(...)` sitting next to (but separate from)
 * the JSON-building code it doesn't structurally depend on.
 *
 * No template may interpolate a money token ({amount}, {total}, or any `{token}` naming a
 * column in `donations`/`pledges`/`drive_financials`): `reminders.description` is
 * member-readable and is copied verbatim into `reminder.created`'s payload, so a template
 * carrying one would launder a donation amount to a `field` rep (docs/schema.md). The
 * controller's draft setter already refuses to hold one; this is the independent write-
 * boundary backstop.
 */
fun buildRuleInsert(orgId: String, id: String, rule: NewAutomationRule): RuleInsert {
    requireNoMoneyToken(rule.descriptionTemplate)
    val actionConfig = buildJsonObject {
        put("assignee", rule.assignee)
        put("description_template", rule.descriptionTemplate)
        put("due_in_days", rule.dueInDays)
        put("add_tag", null as String?)
    }
    val triggerConditions = buildJsonObject {
        rule.minAmountCents?.let { put("min_amount", it / 100) }
    }
    return RuleInsert(
        id = id,
        orgId = orgId,
        name = rule.name,
        triggerVerb = rule.triggerVerb,
        triggerConditions = triggerConditions,
        actionConfig = actionConfig,
    )
}
